import React, { useEffect, useRef, useState } from "react";
// import { useLocation, useNavigate } from 'react-router-dom'; // Uncomment if navigation is needed

// Firebase Imports (ensure '../firebase.js' is configured)
import { db, Storage, auth } from '../firebase'; // Adjust path as needed
import { ref as storageRef, uploadBytes, getDownloadURL, uploadString } from 'firebase/storage';
import { setDoc, doc, collection, serverTimestamp } from 'firebase/firestore';

// UI Imports
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const MailerBagPDFEditor = () => {
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  // const navigate = useNavigate(); // For redirection after save

  // UI State
  const [loading, setLoading] = useState(false); // General loading for file processing
  const [isSaving, setIsSaving] = useState(false); // Specific for product save operation
  const [librariesLoaded, setLibrariesLoaded] = useState(false);

  // Content State
  const [currentFile, setCurrentFile] = useState(null); // Original uploaded file (PDF or image)
  const [contentImageUrl, setContentImageUrl] = useState(null); // Data URL for canvas display
  const [mockupImageUrl, setMockupImageUrl] = useState(null); // For local UI display
  const [modifications, setModifications] = useState([]);

  const [currentImageSize, setCurrentImageSize] = useState({ width: 0, height: 0 }); // Actual design dimensions in inches

  // Product Information
  const [productName, setProductName] = useState(""); // This will be 'designName' in Firestore
  const productNameRef = useRef(''); // To capture product name at time of save

  // Mailer bag settings
  const [bagColor, setBagColor] = useState("white");
  const [bagSize, setBagSize] = useState("11x14");

  // Pricing state
  const [baseCost, setBaseCost] = useState(0);
  const [printCost, setPrintCost] = useState(0); // Cost of printing the design
  const [totalPrice, setTotalPrice] = useState(0);

  const mailerBagImages = {
    white: "/white-mailer-bag.jpg",
    black: "/brown-mailer-bag.jpg",
  };

  const PX_PER_INCH = 24; // Scale used for canvas dimensions from inches

  const sizeConfigs = {
    "11x14": {
      width: 11 * PX_PER_INCH, height: 14 * PX_PER_INCH, label: '11" x 14"', basePrice: 18, printPrice: 0,
      printArea: { x: 30, y: 40, width: 214, height: 296 },
    },
    "13x16": {
      width: 13 * PX_PER_INCH, height: 16 * PX_PER_INCH, label: '13" x 16"', basePrice: 23, printPrice: 0,
      printArea: { x: 20, y: 35, width: 262, height: 354 },
    },
  };

  useEffect(() => {
    productNameRef.current = productName;
  }, [productName]);

  const calculateBaseCost = (size) => sizeConfigs[size]?.basePrice || 0;
  const calculatePrintCost = (size, hasDesign) => hasDesign ? (sizeConfigs[size]?.printPrice || 0) : 0;
  const calculateTotalPrice = (base, print) => base + print;

  useEffect(() => {
    const newBaseCost = calculateBaseCost(bagSize);
    const hasContent = !!contentImageUrl || (fabricCanvasRef.current?.getObjects().some(o => o.isContent) ?? false);
    const newPrintCost = calculatePrintCost(bagSize, hasContent);
    const newTotalPrice = calculateTotalPrice(newBaseCost, newPrintCost);
    setBaseCost(newBaseCost); setPrintCost(newPrintCost); setTotalPrice(newTotalPrice);
  }, [bagSize, contentImageUrl, modifications, librariesLoaded]);

  const updateMockupImageForDisplay = () => { // For UI Mockup state, not necessarily for DB saving
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    const clipRect = canvas.getObjects().find((obj) => obj.isClipRect);
    const originalStroke = clipRect ? clipRect.stroke : null;
    if (clipRect) clipRect.set({ stroke: "transparent" });
    canvas.renderAll();
    const dataURL = canvas.toDataURL({ format: "png", quality: 0.8, multiplier: 1 });
    if (clipRect && originalStroke) clipRect.set({ stroke: originalStroke });
    canvas.renderAll();
    setMockupImageUrl(dataURL);
  };

  // Library Loading
  useEffect(() => {
    const loadScript = (src) => new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src; script.async = true; script.onload = resolve; script.onerror = reject;
      document.head.appendChild(script);
    });
    const loadLibraries = async () => {
      setLoading(true);
      try {
        if (!window.pdfjsLib) {
          await loadScript("https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js");
          if (window.pdfjsLib) window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
          else throw new Error("pdf.js failed to load.");
        }
        if (!window.fabric) {
          await loadScript("https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.0/fabric.min.js");
          if (!window.fabric) throw new Error("Fabric.js failed to load.");
        }
        // PDFLib not directly used in this flow for saving, but kept if other features use it
        if (!window.PDFLib) {
            await loadScript("https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js");
        }
        setLibrariesLoaded(true);
      } catch (error) {
        console.error("Error loading libraries:", error);
        toast.error(`Libs load error: ${error.message}. Refresh.`);
      } finally { setLoading(false); }
    };
    loadLibraries();
  }, []);

  // Canvas Initialization & Event Handlers
  useEffect(() => {
    if (!librariesLoaded || !canvasRef.current || fabricCanvasRef.current) return;
    const canvas = new window.fabric.Canvas(canvasRef.current, { backgroundColor: "#f8f9fa" });
    fabricCanvasRef.current = canvas;

    const updateImageSize = () => {
      const contentImg = canvas.getObjects().find((obj) => obj.isContent && (obj.type === 'image' || obj.isUploadedImage));
      if (contentImg) {
        const sw = contentImg.getScaledWidth(), sh = contentImg.getScaledHeight();
        setCurrentImageSize({ width: parseFloat((sw / PX_PER_INCH).toFixed(2)), height: parseFloat((sh / PX_PER_INCH).toFixed(2)) });
      } else { setCurrentImageSize({ width: 0, height: 0 }); }
    };

    const handleModification = (e) => {
      const obj = e.target;
      if (obj.isMailerBag || !obj.isContent) return;
      const mod = { id: obj.id || Date.now(), type: obj.type, left: obj.left, top: obj.top, scaleX: obj.scaleX, scaleY: obj.scaleY, angle: obj.angle };
      setModifications((prev) => {
        const filtered = prev.filter((m) => m.id !== mod.id);
        return [...filtered, mod];
      });
      setTimeout(() => { updateMockupImageForDisplay(); updateImageSize(); }, 100);
    };

    canvas.on({ 'object:modified': handleModification, 'path:created': () => setTimeout(() => { updateMockupImageForDisplay(); updateImageSize(); }, 100), 'object:added': () => setTimeout(() => { updateMockupImageForDisplay(); updateImageSize(); }, 100), 'object:removed': () => setTimeout(() => { updateMockupImageForDisplay(); updateImageSize(); }, 100), 'object:scaling': updateImageSize });
    
    return () => { fabricCanvasRef.current?.dispose(); fabricCanvasRef.current = null; };
  }, [librariesLoaded]);
  
  const updateOrCreateMailerBag = (canvas, config, color) => {
    const existingBag = canvas.getObjects().find((obj) => obj.isMailerBag);
    const existingClipRect = canvas.getObjects().find((obj) => obj.isClipRect);
    if (existingBag) canvas.remove(existingBag);
    if (existingClipRect) canvas.remove(existingClipRect);

    const imageUrl = mailerBagImages[color];
    window.fabric.Image.fromURL(imageUrl, (img) => {
      if (!img) { console.error("Failed to load mailer bag image:", imageUrl); return; }
      const scaleX = canvas.width / img.width, scaleY = canvas.height / img.height;
      const scale = Math.max(scaleX, scaleY);
      img.set({ left: 0, top: 0, scaleX: scale, scaleY: scale, isMailerBag: true, selectable: false, evented: false, hoverCursor: "default" });
      canvas.add(img).sendToBack(img);

      const clipRect = new window.fabric.Rect({ left: (canvas.width - config.width) / 2, top: (canvas.height - config.height) / 2, width: config.width, height: config.height, fill: "transparent", stroke: "#0066cc", strokeWidth: 2, strokeDashArray: [5, 5], isClipRect: true, selectable: false, evented: false, hoverCursor: "default", excludeFromExport: true });
      canvas.add(clipRect);
      setupClipping(canvas, clipRect);
      canvas.renderAll();
      setTimeout(updateMockupImageForDisplay, 100);
    }, { crossOrigin: "anonymous" });
  };

  const setupClipping = (canvas, clipRect) => {
    const clipPath = new window.fabric.Rect({ left: clipRect.left, top: clipRect.top, width: clipRect.width, height: clipRect.height, absolutePositioned: true });
    canvas.getObjects().filter(obj => obj.isContent).forEach((obj) => obj.clipPath = clipPath);
  };

  const repositionContent = (contentImage, canvas, config) => {
    if (!contentImage) return;
    const clipRect = canvas.getObjects().find((obj) => obj.isClipRect); if (!clipRect) return;
    const maxWidth = clipRect.width * 0.9, maxHeight = clipRect.height * 0.9;
    const scale = Math.min(maxWidth / contentImage.width, maxHeight / contentImage.height, 1);
    contentImage.scale(scale);
    contentImage.set({ left: clipRect.left + (clipRect.width - contentImage.getScaledWidth()) / 2, top: clipRect.top + (clipRect.height - contentImage.getScaledHeight()) / 2 }).setCoords();
  };

  useEffect(() => { // Canvas View Update (Size/Color)
    const canvas = fabricCanvasRef.current; if (!canvas || !librariesLoaded) return;
    const config = sizeConfigs[bagSize];
    let canvasWidth, canvasHeight;
    if (bagSize === "13x16") { canvasWidth = Math.max(config.width * 1.26); canvasHeight = Math.max(config.height * 1.3); }
    else { canvasWidth = Math.max(config.width * 1.2, 300); canvasHeight = Math.max(config.height * 1.2, 400); }
    canvas.setDimensions({ width: canvasWidth, height: canvasHeight });
    canvas.setBackgroundColor("transparent", () => canvas.renderAll());
    updateOrCreateMailerBag(canvas, config, bagColor);
    const contentImg = canvas.getObjects().find((obj) => obj.isContent);
    if (contentImg) repositionContent(contentImg, canvas, config);
    canvas.renderAll(); setTimeout(updateMockupImageForDisplay, 100);
    window.dispatchEvent(new Event("resize"));
  }, [bagSize, bagColor, librariesLoaded]);

  useEffect(() => { // Content Image Loading
    const canvas = fabricCanvasRef.current; if (!canvas || !contentImageUrl || !librariesLoaded) return;
    canvas.getObjects().filter(obj => obj.isContent).forEach((obj) => canvas.remove(obj));
    window.fabric.Image.fromURL(contentImageUrl, (img) => {
      if (!img) return;
      img.set({ isContent: true, isUploadedImage: true, selectable: true, lockUniScaling: false, hasControls: true, hasBorders: true });
      const clipRect = canvas.getObjects().find((obj) => obj.isClipRect);
      if (clipRect) img.clipPath = new window.fabric.Rect({ left: clipRect.left, top: clipRect.top, width: clipRect.width, height: clipRect.height, absolutePositioned: true });
      canvas.add(img); repositionContent(img, canvas, sizeConfigs[bagSize]);
      canvas.setActiveObject(img).renderAll();
      setTimeout(() => {
        updateMockupImageForDisplay();
        const sw = img.getScaledWidth(), sh = img.getScaledHeight();
        setCurrentImageSize({ width: parseFloat((sw / PX_PER_INCH).toFixed(2)), height: parseFloat((sh / PX_PER_INCH).toFixed(2)) });
      }, 100);
    }, { crossOrigin: "anonymous" });
  }, [contentImageUrl, librariesLoaded, bagSize]); // Added bagSize dependency

  const handleFileChange = async (e) => { // This acts as Core Function 1 (handleFileUpload) - Part 1
    if (!librariesLoaded) { toast.warn("Libraries loading..."); return; }
    const file = e.target.files[0]; if (!file) return;
    setLoading(true); setCurrentFile(file); // Store original file
    try {
      let imgURL;
      if (file.type === "application/pdf") { // Call Core Function 2 (convertPdfToImage)
        imgURL = await renderPDFToImageURL(file);
      } else if (file.type.startsWith("image/")) {
        imgURL = await readFileToDataURL(file);
      } else { throw new Error("Unsupported file. Use PDF/image."); }
      setContentImageUrl(imgURL); // Store image URL and trigger canvas render
      toast.success("File processed for preview.");
    } catch (err) {
      console.error("File processing error:", err);
      toast.error(`File load error: ${err.message}`);
    } finally { setLoading(false); if (e.target) e.target.value = null; }
  };

  const renderPDFToImageURL = async (file) => { // Core Function 2 (convertPdfToImage)
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 2.0 }); // Adjust scale for quality
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = viewport.width; tempCanvas.height = viewport.height;
    const ctx = tempCanvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
    return tempCanvas.toDataURL("image/png");
  };

  const readFileToDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject; reader.readAsDataURL(file);
  });
  
  const deleteSelected = () => {
    const c = fabricCanvasRef.current; if (!c) return;
    c.getActiveObjects().filter(obj => obj.isContent).forEach((obj) => c.remove(obj));
    c.discardActiveObject().renderAll(); setTimeout(updateMockupImageForDisplay, 100);
  };

  const clearCanvas = () => {
    const c = fabricCanvasRef.current; if (!c) return;
    c.getObjects().filter(obj => obj.isContent).forEach((obj) => c.remove(obj));
    c.discardActiveObject().renderAll();
    setContentImageUrl(null); setCurrentFile(null); setModifications([]); setMockupImageUrl(null);
    setCurrentImageSize({ width: 0, height: 0 });
  };
  
  // Responsive Scaling
  useEffect(() => {
    const container = canvasContainerRef.current; if (!container || !librariesLoaded) return;
    const resizeObserver = new ResizeObserver(() => {
      const canvas = fabricCanvasRef.current; if (!canvas || !canvas.wrapperEl) return;
      const maxWidth = container.clientWidth;
      const scale = Math.min(maxWidth / canvas.getWidth(), 1);
      canvas.wrapperEl.style.transform = `scale(${scale})`;
      canvas.wrapperEl.style.transformOrigin = "top left";
      // Adjust container height based on scaled canvas
      container.style.height = `${canvas.getHeight() * scale}px`;
    });
    resizeObserver.observe(container);
    // Initial call after a short delay
    const timer = setTimeout(() => {
        if(fabricCanvasRef.current && fabricCanvasRef.current.wrapperEl && container.clientWidth > 0){
            const canvas = fabricCanvasRef.current;
            const maxWidth = container.clientWidth;
            const scale = Math.min(maxWidth / canvas.getWidth(), 1);
            canvas.wrapperEl.style.transform = `scale(${scale})`;
            canvas.wrapperEl.style.transformOrigin = "top left";
            container.style.height = `${canvas.getHeight() * scale}px`;
        }
    }, 250);
    return () => { resizeObserver.disconnect(); clearTimeout(timer); }
  }, [librariesLoaded]);


  // --- Firebase Save Logic ---

  // Upload original PDF (if provided) to Firebase Storage.
  const uploadOriginalPdfToStorage = async (pdfFile, designId, userId) => {
    if (!pdfFile || pdfFile.type !== 'application/pdf') return null;
    // Path: BrandingImages/MailerBag/{userId}/{designId}_original.pdf
    const filePath = `BrandingImages/MailerBag/${designId}_original.pdf`;
    const fileRef = storageRef(Storage, filePath);
    await uploadBytes(fileRef, pdfFile);
    return await getDownloadURL(fileRef);
  };

  // Core Function 3: saveCanvasToStorage (adapted for MailerBags)
  const saveMailerBagMockupToStorage = async (canvasInstance, designId, userId) => {
    if (!canvasInstance) throw new Error("Canvas not available for mockup generation.");
    
    // Temporarily hide the clipping rectangle stroke for a clean export
    const clipRect = canvasInstance.getObjects().find((obj) => obj.isClipRect);
    const originalStroke = clipRect ? clipRect.stroke : null;
    if (clipRect) clipRect.set({ stroke: "transparent" });
    canvasInstance.renderAll();

    const dataURL = canvasInstance.toDataURL({ format: 'png', quality: 0.9, multiplier: 1.5 }); // Good quality mockup

    // Restore the stroke if it was hidden
    if (clipRect && originalStroke) clipRect.set({ stroke: originalStroke });
    canvasInstance.renderAll();

    if (!dataURL) throw new Error("Failed to generate mailer bag mockup image data.");

    // Path: BrandingMockups/MailerBag/{userId}/{designId}_mockup.png
    const filePath = `BrandingMockups/MailerBag/${designId}_mockup.png`;
    const fileRef = storageRef(Storage, filePath);
    await uploadString(fileRef, dataURL, 'data_url');
    return await getDownloadURL(fileRef);
  };

  // Core Function 4: handleSaveProduct
  const handleSaveProduct = async () => {
    if (!auth.currentUser) {
      toast.error("Please log in to save your mailer bag design.");
      return;
    }
    if (!productName.trim()) {
      toast.error("Please enter a Product Name for your design.");
      return;
    }
    const hasContent = !!contentImageUrl || (fabricCanvasRef.current?.getObjects().some(o => o.isContent) ?? false);
    if (!hasContent) {
      toast.error("Please upload a design to your mailer bag before saving.");
      return;
    }

    setIsSaving(true);
    const savingToastId = toast.loading("Saving your mailer bag design...");

    const userId = auth.currentUser.uid;
    const designDocRef = doc(collection(db, "users", userId, "BrandingDesigns"));
    const designId = designDocRef.id;

    let originalPdfUrl = null;
    let mockupMailerBagUrl = null;

    try {
      // 1. Upload Original PDF (if currentFile is a PDF) - Part of `handleFileUpload` concept's storage aspect
      if (currentFile && currentFile.type === 'application/pdf') {
        toast.update(savingToastId, { render: "Uploading original PDF..." });
        originalPdfUrl = await uploadOriginalPdfToStorage(currentFile, designId, userId);
      }

      // 2. Generate and Upload Mockup from Canvas using `saveCanvasToStorage`
      toast.update(savingToastId, { render: "Generating mailer bag mockup..." });
      if (!fabricCanvasRef.current) throw new Error("Canvas is not ready for mockup generation.");
      mockupMailerBagUrl = await saveMailerBagMockupToStorage(fabricCanvasRef.current, designId, userId);

      // 3. Prepare Firestore Payload
const payload = {
  designName: productName.trim(), 
  totalPrice: totalPrice,
  frontDesign: originalPdfUrl, 
  size: bagSize,
  color: bagColor,
  images: {
    front: mockupMailerBagUrl,
  },
  costs: {
    front: printCost,
  },
  Printsizes: {
    front:
      currentImageSize?.width > 0 && currentImageSize?.height > 0
        ? currentImageSize:null,
  },
  productName: "MailerBag", // product.name or a fixed type if needed
  SKU: `MB-${designId}`,
  baseCost: baseCost,
  PrintCost: printCost,
  timestamp: serverTimestamp(),
  userId: userId,
};
 
      
      console.log("Firestore Payload:", payload);

      // 4. Save to Firestore
      toast.update(savingToastId, { render: "Saving design details to database..." });
      await setDoc(designDocRef, payload);

      toast.update(savingToastId, { render: "Mailer bag design saved successfully!", type: "success", isLoading: false, autoClose: 5000 });
      // navigate('/my-designs'); // Optional: redirect user after successful save

    } catch (error) {
      console.error("Error saving mailer bag design:", error);
      toast.update(savingToastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 7000 });
    } finally {
      setIsSaving(false);
    }
  };

  // --- END Firebase Save Logic ---


  const ControlCard = ({ title, children }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-gray-100 ">
      <ToastContainer position="top-right" theme="colored" />
      <header className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm z-10">
        <div className="max-w-screen-2xl mx-auto px-6 py-1">
          <h1 className="text-2xl font-bold text-gray-900">Mailer Bag Customizer</h1>
        </div>
      </header>

      <main className="flex-grow grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 overflow-hidden">
        <aside className="col-span-12 lg:col-span-3 flex flex-col gap-0 overflow-y-auto pb-4">
          <ControlCard title="1. Upload Your Design">
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg" // Accepting images too, though PDF is primary
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
              disabled={!librariesLoaded || loading || isSaving}
            />
            {!librariesLoaded && <div className="mt-2 p-3 text-xs bg-yellow-100 text-yellow-800 rounded">Loading libraries...</div>}
            {loading && <div className="mt-2 p-3 text-xs bg-blue-100 text-blue-800 rounded">Processing your file...</div>}
          </ControlCard>

          <div title="2. Customize Your Bag">
            <div className="space-y-1 pb-4">
              <div>
                <label htmlFor="mailerProductName" className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <input
                  id="mailerProductName" type="text" value={productName} onChange={(e) => setProductName(e.target.value)}
                  placeholder="e.g., Summer Collection Mailer"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={isSaving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bag Size</label>
                <select value={bagSize} onChange={(e) => setBagSize(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" disabled={isSaving}>
                  <option value="11x14">11" x 14"</option>
                  <option value="13x16">13" x 16"</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bag Color</label>
                <div className="flex space-x-2">
                  <button onClick={() => setBagColor("white")} disabled={isSaving}
                    className={`flex-1 py-2 text-sm rounded-md border-2 ${bagColor === "white" ? "border-blue-500 bg-blue-50 text-blue-800 font-semibold" : "border-gray-300 bg-gray-200 text-gray-700"} hover:border-blue-400 disabled:opacity-50`}>White</button>
                  <button onClick={() => setBagColor("black")} disabled={isSaving}
                    className={`flex-1 py-2 text-sm rounded-md border-2 ${bagColor === "black" ? "border-blue-500 bg-blue-50 text-blue-800 font-semibold" : "border-gray-300 bg-gray-200 text-gray-700"} hover:border-blue-400 disabled:opacity-50`}>Black</button>
                </div>
              </div>
            </div>
          </div>
          <ControlCard title="Image Size Info">
            {currentImageSize.width > 0 ? (
              <div className="space-y-1">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Design Size:</span><span className="font-medium text-gray-800">{currentImageSize.width}" × {currentImageSize.height}"</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Print Area:</span><span className="font-medium text-gray-800">{(sizeConfigs[bagSize].printArea.width / PX_PER_INCH).toFixed(1)}" × {(sizeConfigs[bagSize].printArea.height / PX_PER_INCH).toFixed(1)}"</span></div>
              </div>
            ) : ( <div className="text-sm text-gray-500 text-center py-2">No image loaded</div>)}
          </ControlCard>
        </aside>

        <section className="col-span-12 lg:col-span-5 flex flex-col bg-white rounded-lg shadow-inner border border-gray-200 overflow-hidden">
          <div ref={canvasContainerRef} className="flex-grow flex items-top pt-10 justify-center p-3 bg-gray-100 overflow-auto relative">
            {isSaving && <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20"><p className="text-blue-600 font-semibold">Saving design...</p></div>}
            <div className="relative w-min h-min shadow-md"> <canvas ref={canvasRef} /> </div>
          </div>
        </section>

        <aside className="col-span-12 lg:col-span-4 flex flex-col gap-0 overflow-y-auto pb-4">
          <ControlCard title="Pricing Summary">
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span className="text-gray-600">Print Size:</span><span className="font-medium text-gray-800">{sizeConfigs[bagSize].label}</span></div>
              <hr />
              <div className="flex justify-between text-sm"><span className="text-gray-600">Base Cost (Bag):</span><span className="font-medium text-gray-800">₹{baseCost.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-600">Print Cost (Design):</span><span className="font-medium text-gray-800">₹{printCost.toFixed(2)}</span></div>
              <hr className="my-2 border-dashed" />
              <div className="flex justify-between items-center text-lg font-bold text-blue-800"><span>Total Price:</span><span>₹{totalPrice.toFixed(2)}</span></div>
            </div>
          </ControlCard>

          <ControlCard title="Editing Tools">
            <div className="grid grid-cols-2 gap-3">
              <button onClick={deleteSelected} disabled={!librariesLoaded || isSaving}
                className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 text-sm font-medium">Delete Selected</button>
              <button onClick={clearCanvas} disabled={!librariesLoaded || isSaving}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 text-sm font-medium">Clear Design</button>
            </div>
          </ControlCard>

          {/* SAVE PRODUCT BUTTON */}
          <ControlCard title="Finalize & Save">
            <button
                onClick={handleSaveProduct}
                disabled={!librariesLoaded || isSaving || loading || !productName.trim() || !(!!contentImageUrl || (fabricCanvasRef.current?.getObjects().some(o => o.isContent) ?? false))}
                className="w-full px-4 py-3 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
                {isSaving ? 'Saving...' : 'Save Mailer Bag Design'}
            </button>
            {!auth.currentUser && <p className="text-xs text-red-500 mt-2 text-center">Please log in to save.</p>}
          </ControlCard>

          {/* Optional Local Export Options - Can be removed if not needed for production */}
          {/*
          <ControlCard title="Local Export Options">
            <div className="space-y-2">
              <button onClick={exportAsImage} disabled={!librariesLoaded || isSaving} className="w-full px-4 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:opacity-50">Export High-Res Image</button>
              <button onClick={downloadPDF} disabled={!librariesLoaded || isSaving || !currentFile || currentFile.type !== "application/pdf"} className="w-full px-4 py-2 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50">Download Original PDF</button>
              <button onClick={copyJSONToClipboard} disabled={!librariesLoaded || isSaving} className="w-full px-4 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 disabled:opacity-50">Copy JSON Data</button>
            </div>
          </ControlCard>
          */}
        </aside>
      </main>
    </div>
  );
};

export default MailerBagPDFEditor;